// ============================================================================
// LE RELEVÉ « À TRAITER », UNE FOIS POUR TOUTE LA CONSOLE — la cloche de la barre du haut et les compteurs du menu
// lisent le MÊME relevé (décision du 12/10 : « les deux, calculés à partir de la même liste »).
// ⚠️ UN SEUL RELEVÉ, pas un par lecteur : deux relevés faits à des instants différents annonceraient deux nombres pour
//    la même chose, et chacun interroge six sources.
// ============================================================================
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Badge } from '@orgazone/ui-web';
import { fetchTriageCounts, triageByRoute, triageItems, type TriageCounts } from './triage';

const TriageContext = createContext<TriageCounts | null>(null);

/** Relève les compteurs au montage puis toutes les 60 s, pour tout ce qui est rendu dessous. */
export function TriageProvider({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<TriageCounts | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => { void fetchTriageCounts().then((c) => { if (alive) setCounts(c); }); };
    load();
    const h = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return <TriageContext.Provider value={counts}>{children}</TriageContext.Provider>;
}

/** Le dernier relevé, ou `null` tant que le premier n'est pas revenu. */
export const useTriageCounts = (): TriageCounts | null => useContext(TriageContext);

const TONE = { da: 'danger', wa: 'warn', in: 'info' } as const;

/**
 * Le compteur d'une entrée du menu : ce qui l'attend dans la liste « À traiter ». Rien tant que le relevé n'est pas
 * revenu, rien quand rien n'attend — un « 0 » dans le menu ne dirait rien.
 */
export function NavCount({ to }: { to: string }) {
  const counts = useTriageCounts();
  if (!counts) return null;
  const hit = triageByRoute(triageItems(counts).items).get(to);
  if (!hit) return null;
  return (
    <Badge size="xs" tone={TONE[hit.kind]} srLabel={`${hit.n} à traiter`} style={{ flexShrink: 0 }}>
      {/* Le chiffre se VOIT, la phrase se LIT : sans aria-hidden, une aide vocale dirait « 3 3 à traiter ». */}
      <span aria-hidden>{hit.n > 99 ? '99+' : hit.n}</span>
    </Badge>
  );
}
